'use no memo';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import type { Extension } from '@confluence/macro-tracker/entry-points/types.ts';
import { canUseMultiMacroQuery } from '@confluence/fabric-extension-queries/entry-points/useMultiMacroQuery.ts';
import { useMultiMacroQuery } from '@confluence/fabric-extension-queries/entry-points/multiMacroQuery.ts';
import { getMacroQueryVariables } from '@confluence/fabric-extension-queries/entry-points/getMacroQueryVariables.ts';
import {
	MacroBodyRendererQueryWithTags,
	MacroContentRendererQueryWithTags,
} from '@confluence/fabric-extension-queries/entry-points/MacroContentRendererQueryWithTags.graphql.ts';

export const useIndividualMacroContentData = (contentId: string, node: Extension) => {
	const { isMacroContentAndBody, ...rest } = getMacroQueryVariables({
		node,
		contentId,
	});

	const MacroQuery = isMacroContentAndBody
		? MacroBodyRendererQueryWithTags
		: MacroContentRendererQueryWithTags;

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const { data } = useQuery(MacroQuery, {
		fetchPolicy: 'cache-only',
		variables: rest,
	});

	return data;
};

export const useMultiMacroContentData = (contentId: string, node: Extension) => {
	const macroId = node.parameters?.macroMetadata?.macroId?.value;
	const { renderedMacro } = useMultiMacroQuery({ contentId, macroId });

	return {
		macroBodyRenderer: renderedMacro,
		representation: renderedMacro?.macroRenderedRepresentation,
		mediaToken: renderedMacro?.mediaToken,
		webResourceDependencies: renderedMacro?.webResource,
		macroBodyStorageFormat: { value: renderedMacro?.macroBodyStorage },
	};
};

export const useMacroContentData = (contentId: string, node: any) => {
	return canUseMultiMacroQuery(node.extensionKey)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useMultiMacroContentData(contentId, node)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useIndividualMacroContentData(contentId, node);
};
